/**
 * Portal Manta · ContextoLotePage
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { V, S, calcKmUrbano, calcInterseccoesPond, extKm, capexAmpliacao, capexOAE, capexPassarela, capexEdif, FonteBadge } from '../../globals.js';
import { CONTEXTO_LOTES } from '../../data/contexto-lotes.js';
import { calcPraticabilidade } from '../agentes/calcPraticabilidade.jsx';
import { FonteModal } from '../charts/FonteModal.jsx';
import { KPICard } from '../charts/KPICard.jsx';
import { StackedBar } from '../charts/StackedBar.jsx';
import { PluviChart } from '../charts/PluviChart.jsx';
import { Gauge } from '../charts/Gauge.jsx';
import { RodoviaDecomp } from '../lotes-vis/RodoviaDecomp.jsx';
import { TipoObraConsol } from '../lotes-vis/TipoObraConsol.jsx';
import { TipoObraMatrix } from '../lotes-vis/TipoObraMatrix.jsx';
import { ConclusoesBox } from '../charts/ConclusoesBox.jsx';
import { M } from '../../tokens/colors.js';
import { F } from '../../tokens/fonts.js';

const ContextoLotePage = memo(function ContextoLotePage_Inner({lote}){
  const [modal, setModal] = useState(null); // {fonte, label}
  const ctx = CONTEXTO_LOTES[lote.k] || {};

  // Cálculos memoizados
  const ext = useMemo(()=>extKm(lote), [lote]);
  const kmUrb = useMemo(()=>calcKmUrbano(lote), [lote]);
  const kmNUrb = ext - kmUrb;
  const pctUrb = ext>0 ? (kmUrb/ext*100) : 0;
  const intPond = useMemo(()=>calcInterseccoesPond(lote), [lote]);
  const prat = useMemo(()=>calcPraticabilidade(lote), [lote]);
  const capExAmpl = useMemo(()=>capexAmpliacao(lote), [lote]); // R$ M
  // Peso urbano do CAPEX de ampliação: assume 1.7× custo médio em zona urbana
  const fatorUrb = 1.7;
  const denomEq = kmNUrb + kmUrb*fatorUrb; // km equivalentes
  const rsKmNUrb = denomEq>0 ? capExAmpl/denomEq : 0;          // R$ M / km não-urbano
  const rsKmUrb = rsKmNUrb*fatorUrb;                             // R$ M / km urbano

  const oaeM2 = ctx.oae_m2_total||0;
  const capOAE = capexOAE(lote); // R$ M
  const rsM2OAE = oaeM2>0 ? (capOAE*1e6/oaeM2) : 0; // R$ / m²
  const oaeM2porKm = ext>0 ? oaeM2/ext : 0;

  const passM2 = ctx.passarela_m2_total||0;
  const capPass = capexPassarela(lote);
  const rsM2Pass = passM2>0 ? (capPass*1e6/passM2) : 0;

  const edifM2 = ctx.edif_m2?.total||0;
  const capEdif = capexEdif(lote);
  const rsM2Edif = edifM2>0 ? (capEdif*1e6/edifM2) : 0;

  const oaesUn = (lote.dados_manta?.obras_per?.oaes||[]).length;
  const intUn = ['trombetas','diamantes','parclos','trevos','rotatorias']
    .reduce((s,k)=>s+(lote.dados_manta?.obras_per?.intersecoes?.[k]?.length||0),0);

  const fmt2 = (n)=> (n||0).toLocaleString('pt-BR',{minimumFractionDigits:0,maximumFractionDigits:0});
  const fmt1 = (n)=> (n||0).toLocaleString('pt-BR',{minimumFractionDigits:1,maximumFractionDigits:1});

  const onFonte = (m)=>setModal(m);

  return (<div style={S.page}>
    {modal && <FonteModal {...modal} onClose={()=>setModal(null)}/>}

    {/* Header da página */}
    <div style={{marginBottom:16}}>
      <div style={S.h1}>Contexto do Lote · {lote.sig}</div>
      <div style={S.sub}>KPIs físicos e socioeconômicos que dão sentido ao R$/km. Cada indicador rastreável até a fonte. <span style={{color:V.or,fontWeight:600}}>📍 = clique para rastreabilidade.</span></div>
    </div>

    {/* ─── FAMÍLIA 1: EXTENSÃO & TIPOLOGIA ───────────────────── */}
    <div style={{...S.cardP, marginBottom:16, padding:'18px 20px'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
        <div style={{width:24,height:24,borderRadius:6,background:V.or,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700}}>1</div>
        <div style={{fontSize:13,fontWeight:700,color:V.br}}>Extensão & Tipologia Espacial</div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:14}}>
        <KPICard label="Extensão Total" value={fmt1(ext)} unit="km" color={V.br}
          fonte={{doc:'PER · Anexo 2',pag:'Resumo PER',data:ctx.f_km_urb?.data,aut:'ANTT'}} onFonte={onFonte}/>
        <KPICard label="Km Urbano" value={fmt1(kmUrb)} unit="km" color={V.or}
          sub={`${pctUrb.toFixed(1)}% da extensão`}
          fonte={ctx.f_km_urb} onFonte={onFonte}/>
        <KPICard label="Km Não-Urbano" value={fmt1(kmNUrb)} unit="km" color={V.gn}
          sub={`${(100-pctUrb).toFixed(1)}% da extensão`}
          fonte={ctx.f_km_urb} onFonte={onFonte}/>
        <KPICard label="CAPEX Ampliação" value={fmt2(capExAmpl)} unit="R$ M" color={V.br}
          sub="A1+A2+A3+A5+A9+A10 (s/ TI/REC/MANUT)"
          fonte={{doc:'MEF · Tipologias',pag:'Aba "Capex Hierárquico"',data:'2024-01-31',aut:'ANTT'}} onFonte={onFonte}/>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:14}}>
        <div style={{background:V.g0,border:'1px solid '+V.g2,borderRadius:8,padding:'12px 14px'}}>
          <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:8}}>Custo Unitário Segregado <FonteBadge fonte={{doc:'Cálculo Manta',pag:'fator urbano = 1.7× rural (premissa Manta)',data:'2025-01-01',aut:'Manta · Custos'}} onClick={()=>onFonte({fonte:{doc:'Cálculo Manta',pag:'fator urbano = 1.7× rural — premissa baseada em sondagem de mercado de obras anteriores PR/SP',data:'2025-01-01',aut:'Manta · Custos'},label:'Custo unitário segregado'})}/></div>
          <div style={{fontSize:10.5,color:V.g4,marginBottom:8}}>Premissa: km urbano custa 1,7× o rural (desapropriação, interferências, OAEs urbanas)</div>
          <table style={{width:'100%',fontSize:11.5}}><tbody>
            <tr><td style={{padding:'5px 0',color:V.g5}}>R$/km não-urbano</td><td style={{padding:'5px 0',textAlign:'right',fontWeight:700,color:V.gn,fontFamily:'monospace'}}>R$ {fmt1(rsKmNUrb)} M</td></tr>
            <tr><td style={{padding:'5px 0',color:V.g5}}>R$/km urbano</td><td style={{padding:'5px 0',textAlign:'right',fontWeight:700,color:V.or,fontFamily:'monospace'}}>R$ {fmt1(rsKmUrb)} M</td></tr>
            <tr style={{borderTop:'1px solid '+V.g2}}><td style={{padding:'5px 0',color:V.g5,fontSize:10}}>Razão urbano/rural</td><td style={{padding:'5px 0',textAlign:'right',fontSize:10,color:V.g5}}>{fatorUrb.toFixed(2)}×</td></tr>
          </tbody></table>
        </div>

        <div style={{background:V.g0,border:'1px solid '+V.g2,borderRadius:8,padding:'12px 14px'}}>
          <div style={{fontSize:11,fontWeight:700,color:V.br,marginBottom:8}}>Densidade de Obras</div>
          <table style={{width:'100%',fontSize:11.5}}><tbody>
            <tr><td style={{padding:'5px 0',color:V.g5}}>OAEs (m²/km)</td><td style={{padding:'5px 0',textAlign:'right',fontWeight:700,color:V.br,fontFamily:'monospace'}}>{fmt1(oaeM2porKm)} m²/km</td></tr>
            <tr><td style={{padding:'5px 0',color:V.g5}}>OAEs (un/km)</td><td style={{padding:'5px 0',textAlign:'right',color:V.g5,fontFamily:'monospace'}}>{(oaesUn/ext).toFixed(2)} un/km</td></tr>
            <tr><td style={{padding:'5px 0',color:V.g5}}>Interseções pond/km</td><td style={{padding:'5px 0',textAlign:'right',fontWeight:700,color:V.br,fontFamily:'monospace'}}>{(intPond/ext).toFixed(2)}</td></tr>
            <tr><td style={{padding:'5px 0',color:V.g4,fontSize:10}}>(soltas: {intUn} un · pond: {intPond.toFixed(0)})</td><td/></tr>
          </tbody></table>
        </div>
      </div>
    </div>

    {/* ─── BLOCO MEF — CONVERSÕES PARA UNIDADE CORRETA ──────── */}
    <div style={{...S.cardP, marginBottom:16, padding:'18px 20px', background:'linear-gradient(180deg,#FDF7F2,#fff)'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
        <div style={{width:24,height:24,borderRadius:6,background:V.br,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700}}>★</div>
        <div style={{fontSize:13,fontWeight:700,color:V.br}}>Unidade-MEF — itens convertidos de "un" para "m²"</div>
      </div>
      <div style={{fontSize:10.5,color:V.g5,marginBottom:14}}>"un" esconde o tamanho. R$/m² é comparável entre lotes e fiel ao MEF.</div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
        <div style={{...S.cardP, padding:'12px 14px'}}>
          <div style={{fontSize:10,fontWeight:700,color:V.or,letterSpacing:.5,textTransform:'uppercase',marginBottom:4}}>OAE (A9) <FonteBadge fonte={ctx.f_oae_m2} onClick={()=>onFonte({fonte:ctx.f_oae_m2,label:'OAE — área de tabuleiro'})}/></div>
          <div style={{fontSize:18,fontWeight:700,color:V.br,fontFamily:'monospace'}}>{fmt2(oaeM2)} m²</div>
          <div style={{fontSize:10,color:V.g4,marginTop:2}}>{oaesUn} OAEs · média {fmt2(oaesUn>0?oaeM2/oaesUn:0)} m²/un</div>
          <div style={{borderTop:'1px solid '+V.g2,marginTop:8,paddingTop:8,display:'flex',justifyContent:'space-between'}}>
            <span style={{fontSize:10,color:V.g5}}>R$/m²</span>
            <span style={{fontSize:13,fontWeight:700,color:V.or,fontFamily:'monospace'}}>R$ {fmt2(rsM2OAE)}</span>
          </div>
        </div>

        <div style={{...S.cardP, padding:'12px 14px'}}>
          <div style={{fontSize:10,fontWeight:700,color:V.or,letterSpacing:.5,textTransform:'uppercase',marginBottom:4}}>Passarelas (A10) <FonteBadge fonte={ctx.f_pass_m2} onClick={()=>onFonte({fonte:ctx.f_pass_m2,label:'Passarelas — área'})}/></div>
          <div style={{fontSize:18,fontWeight:700,color:V.br,fontFamily:'monospace'}}>{fmt2(passM2)} m²</div>
          <div style={{fontSize:10,color:V.g4,marginTop:2}}>{(lote.tipologias||[]).find(t=>t.cod==='A10')?.qtd||0} un</div>
          <div style={{borderTop:'1px solid '+V.g2,marginTop:8,paddingTop:8,display:'flex',justifyContent:'space-between'}}>
            <span style={{fontSize:10,color:V.g5}}>R$/m²</span>
            <span style={{fontSize:13,fontWeight:700,color:V.or,fontFamily:'monospace'}}>R$ {fmt2(rsM2Pass)}</span>
          </div>
        </div>

        <div style={{...S.cardP, padding:'12px 14px'}}>
          <div style={{fontSize:10,fontWeight:700,color:V.or,letterSpacing:.5,textTransform:'uppercase',marginBottom:4}}>Edificações (E1–E9) <FonteBadge fonte={ctx.f_edif_m2} onClick={()=>onFonte({fonte:ctx.f_edif_m2,label:'Edificações — área construída'})}/></div>
          <div style={{fontSize:18,fontWeight:700,color:V.br,fontFamily:'monospace'}}>{fmt2(edifM2)} m²</div>
          <div style={{fontSize:10,color:V.g4,marginTop:2}}>Pedágio {fmt2(ctx.edif_m2?.pedagio||0)} · BSO+SAU {fmt2(ctx.edif_m2?.bso_sau||0)}</div>
          <div style={{borderTop:'1px solid '+V.g2,marginTop:8,paddingTop:8,display:'flex',justifyContent:'space-between'}}>
            <span style={{fontSize:10,color:V.g5}}>R$/m²</span>
            <span style={{fontSize:13,fontWeight:700,color:V.or,fontFamily:'monospace'}}>R$ {fmt2(rsM2Edif)}</span>
          </div>
        </div>
      </div>

      <div style={{marginTop:12,padding:'10px 12px',background:V.orp,borderRadius:6,fontSize:10.5,color:V.br2}}>
        <strong>Interseções ponderadas:</strong> trombeta=1,5 · diamante=1,2 · parclo=1,0 · trevo=2,0 — total ponderado <strong>{intPond.toFixed(0)}</strong> contra {intUn} unidades soltas. Compensa diferenças de tipologia que `un` não captura.
      </div>
    </div>

    {/* ─── FAMÍLIA 2: CONDICIONANTES FÍSICAS ────────────────── */}
    <div style={{...S.cardP, marginBottom:16, padding:'18px 20px'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
        <div style={{width:24,height:24,borderRadius:6,background:V.gn,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700}}>2</div>
        <div style={{fontSize:13,fontWeight:700,color:V.br}}>Condicionantes Físicas</div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:18}}>
        {/* Solo */}
        <div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:V.g7}}>Tipo de Solo Dominante (% da extensão)</div>
            <FonteBadge fonte={ctx.f_solo} onClick={()=>onFonte({fonte:ctx.f_solo,label:'Tipo de solo dominante'})}/>
          </div>
          <StackedBar segments={[
            {label:'LRA', value:ctx.solo?.LRA||0, color:'#A0522D'},
            {label:'LVA', value:ctx.solo?.LVA||0, color:'#CD853F'},
            {label:'Saprolítico', value:ctx.solo?.saprolitico||0, color:'#D4682A'},
            {label:'Colúvio', value:ctx.solo?.coluvio||0, color:'#DAA520'},
            {label:'Aluvionar', value:ctx.solo?.aluvionar||0, color:'#5F9EA0'},
            {label:'Residual', value:ctx.solo?.residual||0, color:'#708090'},
          ].filter(s=>s.value>0)}/>
          <div style={{marginTop:10,padding:'8px 10px',background:V.g0,borderRadius:6,fontSize:10.5,color:V.g5}}>
            NSPT médio nas OAEs: <strong style={{color:V.br}}>{ctx.nspt_medio_oae||'—'} golpes/30cm</strong>
            <FonteBadge fonte={ctx.f_nspt} onClick={()=>onFonte({fonte:ctx.f_nspt,label:'NSPT médio'})}/>
          </div>
        </div>

        {/* Topografia */}
        <div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:V.g7}}>Topografia (% da extensão)</div>
            <FonteBadge fonte={ctx.f_topo} onClick={()=>onFonte({fonte:ctx.f_topo,label:'Topografia'})}/>
          </div>
          <StackedBar segments={[
            {label:'Plano', value:ctx.topo?.plano||0, color:V.gn},
            {label:'Ondulado', value:ctx.topo?.ondulado||0, color:V.or},
            {label:'Montanhoso', value:ctx.topo?.montanhoso||0, color:'#C45A2B'},
          ]}/>
        </div>

        {/* Pluviometria */}
        <div style={{gridColumn:'1 / -1'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:V.g7}}>Pluviometria — média mensal (mm) · total anual <strong style={{color:V.bl}}>{ctx.pluv_mm_ano} mm/ano</strong></div>
            <FonteBadge fonte={ctx.f_pluv} onClick={()=>onFonte({fonte:ctx.f_pluv,label:'Pluviometria mensal'})}/>
          </div>
          <PluviChart mensal={ctx.pluv_mensal||[]}/>
        </div>
      </div>
    </div>

    {/* ─── FAMÍLIA 3: PRATICABILIDADE ───────────────────────── */}
    <div style={{...S.cardP, marginBottom:16, padding:'18px 20px', background:'linear-gradient(180deg,#FAFBFC,#fff)'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
        <div style={{width:24,height:24,borderRadius:6,background:V.bl,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700}}>3</div>
        <div style={{fontSize:13,fontWeight:700,color:V.br}}>Praticabilidade — KPI síntese (0 = inviável · 100 = ideal)</div>
      </div>
      <div style={{fontSize:10.5,color:V.g5,marginBottom:14}}>Combina os itens 1 e 2 com pesos por disciplina · permite ranking entre lotes do PR.</div>

      <div style={{display:'grid',gridTemplateColumns:'1.4fr 1fr 1fr 1fr 1fr',gap:14,alignItems:'center'}}>
        <div style={{textAlign:'center',padding:'14px',background:V.g0,borderRadius:10,border:'2px solid '+(prat.total>=70?V.gn:prat.total>=50?V.or:'#C45A2B')}}>
          <div style={{fontSize:10,fontWeight:700,color:V.g5,textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>ÍNDICE TOTAL</div>
          <div style={{fontSize:42,fontWeight:700,color:prat.total>=70?V.gn:prat.total>=50?V.or:'#C45A2B',fontFamily:'monospace',lineHeight:1}}>{prat.total}</div>
          <div style={{fontSize:10,color:V.g5,marginTop:4}}>{prat.total>=70?'praticável':prat.total>=50?'moderado':'restritivo'}</div>
        </div>
        <Gauge value={prat.geo} label="Geotécnica · 30%" size={110}/>
        <Gauge value={prat.hidro} label="Hidrológica · 20%" size={110}/>
        <Gauge value={prat.fund} label="Fundiária · 20%" size={110}/>
        <Gauge value={prat.constr} label="Construtiva · 30%" size={110}/>
      </div>

      <div style={{marginTop:14,padding:'10px 12px',background:V.g0,borderRadius:6,fontSize:10.5,color:V.g5}}>
        <strong>Drivers do índice:</strong> Geo (NSPT médio) · Hidro (mm/ano) · Fundiária (% urbano) · Construtiva (topo + OAE/km).
        <FonteBadge fonte={{doc:'Metodologia Manta',pag:'Modelo de Praticabilidade — fórmula consolidada',data:'2025-01-01',aut:'Manta · Engenharia'}} onClick={()=>onFonte({fonte:{doc:'Metodologia Manta',pag:'Modelo de Praticabilidade — pesos: geo 30%, hidro 20%, fund 20%, constr 30%. Fórmulas no SKILL.md interno.',data:'2025-01-01',aut:'Manta · Engenharia'},label:'Metodologia do índice de praticabilidade'})}/>
      </div>
    </div>

    {/* ─── v21 · DECOMPOSIÇÃO POR RODOVIA ───────────────────── */}
    <RodoviaDecomp lote={lote} capExAmpl={capExAmpl} onFonte={onFonte}/>

    {/* ─── v22 · DECOMPOSIÇÃO POR TIPO DE OBRA ──────────────── */}
    <div style={{...S.cardP, marginBottom:16, padding:'18px 20px'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
        <div style={{width:24,height:24,borderRadius:6,background:V.bl,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700}}>5</div>
        <div style={{fontSize:13,fontWeight:700,color:V.br}}>Decomposição por Tipo de Obra de Ampliação</div>
      </div>
      <div style={{fontSize:10.5,color:V.g5,marginBottom:12}}>Consolidado em cards (R$ por tipo) + matriz física por rodovia (km/un por tipo). Clique no card para a rastreabilidade da tipologia.</div>
      <TipoObraConsol lote={lote} onFonte={onFonte}/>
      <TipoObraMatrix lote={lote} onFonte={onFonte}/>
    </div>

    {/* ─── v21 · CONCLUSÕES — DRIVERS PRINCIPAIS ────────────── */}
    <ConclusoesBox lote={lote} onFonte={onFonte}/>

  </div>);
});

export { ContextoLotePage };
export default ContextoLotePage;
